import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { InputField, Button } from '../components/ui';
import { useToast } from '../context/ToastContext';
import { api } from '../lib/api';

interface LoginProps {
  onLogin: (user: any) => void;
  onSwitchToRegister: () => void;
}

const Login: React.FC<LoginProps> = ({ onLogin, onSwitchToRegister }) => {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    if (!email || !password) {
      addToast('error', 'Validation Error', 'Email and password are required.');
      return;
    }

    setLoading(true);

    try {
      const data = await api.auth.login(email, password);
      const user = { ...data.user, token: data.token };
      onLogin(user);
      addToast('success', 'Welcome Back', `Logged in as ${user.name}`);
    } catch (err: any) {
      addToast('error', 'Login Failed', err.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleLogin();
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg-base)',
      backgroundImage:
        'radial-gradient(ellipse at 20% 0%, var(--body-gradient-1) 0%, transparent 50%), radial-gradient(ellipse at 80% 100%, var(--body-gradient-2) 0%, transparent 50%)',
      padding: '20px',
    }}>
      <div style={{
        width: '100%',
        maxWidth: '420px',
        background: 'var(--modal-bg)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-xl)',
        padding: '40px 36px',
        boxShadow: 'var(--modal-shadow)',
        animation: 'modalIn 0.4s ease',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <img src="/logo.png" alt="VOP CRM" width={140} style={{ flexShrink: 0 }} />
        </div>

        {/* Form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }} onKeyDown={handleKeyDown}>
          <InputField
            label="Email"
            type="email"
            placeholder="you@company.com"
            icon="solar:letter-bold-duotone"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <InputField
            label="Password"
            type="password"
            placeholder="Enter your password"
            icon="solar:lock-bold-duotone"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button style={{
              background: 'none', border: 'none', color: 'var(--primary-500)', fontSize: '13px',
              cursor: 'pointer', fontFamily: 'var(--font-body)', padding: 0,
            }}>
              Forgot password?
            </button>
          </div>

          <Button variant="primary" fullWidth loading={loading} onClick={handleLogin}
            style={{ marginTop: '8px', padding: '12px 20px' }}>
            Sign In
          </Button>
        </div>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '24px 0' }}>
          <div style={{ flex: 1, height: '1px', background: 'var(--glass-border)' }} />
          <span style={{ color: 'var(--dark-600)', fontSize: '12px' }}>OR</span>
          <div style={{ flex: 1, height: '1px', background: 'var(--glass-border)' }} />
        </div>

        {/* Switch to Client Portal */}
        <div style={{ textAlign: 'center' }}>
          <span style={{ color: 'var(--dark-400)', fontSize: '14px' }}>Are you a client? </span>
          <button onClick={() => navigate('/portal/login')} style={{
            background: 'none', border: 'none', color: 'var(--primary-500)', fontSize: '14px',
            fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)',
          }}>
            Click Here
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;
